import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import type { SxProps } from "@mui/material/styles";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import TextField from "@mui/material/TextField";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { createContext, type FC, useContext, useMemo, useState } from "react";
import { vcAccountAssignments } from "src/queries/vc-account-assignments";
import { ReferralPartner, vendors } from "src/queries/vendors";
import theme from "src/styles/theme";
import { EMPTY_ARRAY } from "src/utils/empty-values";
import { DeleteVcAccountAssignmentDialog } from "./DeleteVcAccountAssignmentDialog";
import { NewVcAccountAssignmentsDialog } from "./NewVcAccountAssignmentsDialog/NewVcAccountAssignmentsDialog";
import type { AssignedVcAccount } from "./vc-account-assignments-types";
import usePermissions from "src/components/CustomHooks/usePermissions";
import Tooltip from "@mui/material/Tooltip";

const DeleteTargetContext = createContext<{
  setDeleteTarget(vcAccount: AssignedVcAccount): void;
}>({
  setDeleteTarget() {},
});

const PartnerDisplay: FC<{ partnerId: string | null }> = ({ partnerId }) => {
  const { data: partnerOptions, isPending } = useQuery({ ...vendors.referralPartners, enabled: !!partnerId });
  const displayText = useMemo(() => {
    if (!partnerId) return "";
    if (isPending) return "Loading...";
    return partnerOptions?.find(partner => partner.id.toString() === partnerId)?.name || "Unknown partner";
  }, [partnerId, isPending, partnerOptions]);
  return <span>{displayText}</span>;
};

const DeleteButton: FC<{ vcAccount: AssignedVcAccount }> = ({ vcAccount }) => {
  const { setDeleteTarget } = useContext(DeleteTargetContext);
  return (
    <Button
      variant="contained"
      sx={{ paddingBlock: 0.5, textTransform: "none" }}
      onClick={() => setDeleteTarget(vcAccount)}
    >
      Cancel
    </Button>
  );
};

const gridColumns: GridColDef<AssignedVcAccount>[] = [
  { field: "name", headerName: "Name", width: 150 },
  { field: "email", headerName: "Email", width: 200 },
  { field: "vendorName", headerName: "Vendor", width: 200 },
  { field: "storeName", headerName: "Store", width: 200 },
  {
    field: "partnerId",
    headerName: "Partner",
    width: 200,
    filterable: false,
    renderCell: ({ value }) => <PartnerDisplay partnerId={value} />,
  },
  { field: "notes", headerName: "Notes", width: 250, flex: 1 },
  {
    field: "actions",
    type: "actions",
    headerName: "Actions",
    display: "flex",
    getActions: ({ row }) => [<DeleteButton vcAccount={row} />],
  },
];

const gridStyle: SxProps = {
  borderColor: "transparent",
  "& .MuiDataGrid-main": {
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  "& .MuiDataGrid-columnHeader": {
    borderRight: "1px solid #e0e0e0",

    ".MuiButtonBase-root": {
      marginTop: "0",
      marginBottom: "0",
    },
  },
  "& .MuiFormControl-root": {
    label: {
      display: "none",
    },
    "& .MuiInputBase-root": {
      marginTop: "0",
    },
  },
};

export const VcAccountAssignmentsPage: FC = () => {
  const { hasPermission } = usePermissions();
  const canAssignAccounts = hasPermission(["admin:*", "user:client_config:write:*"]);

  const assignedUsersQuery = useQuery(vcAccountAssignments.assigned);
  const [isAssignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AssignedVcAccount | null>(null);
  const deleteTargetContextValue = useMemo(() => ({ setDeleteTarget }), []);
  const [filterPartner, setFilterPartner] = useState<ReferralPartner | null>(null);

  const partnerOptionsQuery = useQuery(vendors.referralPartners);
  const partnerOptions = useMemo(
    () => [{ id: -1, name: "Show assignments with no partner" }, ...(partnerOptionsQuery.data ?? EMPTY_ARRAY)],
    [partnerOptionsQuery.data],
  );

  const filteredAssignedUsers = useMemo(() => {
    if (!assignedUsersQuery.data) return EMPTY_ARRAY;
    if (!filterPartner) return assignedUsersQuery.data;
    if (filterPartner.id === -1) {
      return assignedUsersQuery.data.filter(account => account.partnerId == null);
    }
    const targetId = filterPartner.id.toString();
    return assignedUsersQuery.data.filter(account => account.partnerId === targetId);
  }, [assignedUsersQuery.data, filterPartner]);

  const filteredGridColumns = useMemo(() => {
    if (canAssignAccounts) return gridColumns;
    return gridColumns.filter(column => column.field !== "actions");
  }, [canAssignAccounts]);

  return (
    <ThemeProvider theme={theme}>
      <DeleteTargetContext.Provider value={deleteTargetContextValue}>
        <Box p={2}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <Tooltip title={canAssignAccounts ? "" : "You do not have permission to assign accounts"}>
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setAssignmentDialogOpen(true)}
                    disabled={!canAssignAccounts}
                  >
                    Assign Accounts
                  </Button>
                </span>
              </Tooltip>
            </div>
            <FormControl
              sx={{
                flexDirection: "row",
                alignItems: "center",
                gap: "1rem",
                minWidth: "24rem",
                whiteSpace: "nowrap",
              }}
            >
              <Autocomplete
                value={filterPartner}
                options={partnerOptions}
                sx={{ paddingBlock: 0 }}
                onChange={(_, newValue) => setFilterPartner(newValue)}
                slotProps={{ paper: { sx: { paddingBlock: 0 } } }}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={partnerOptionsQuery.isPending ? <>Loading partners...</> : <>Filter for partner</>}
                    sx={{ paddingBlock: 0 }}
                  />
                )}
                getOptionLabel={option => option.name}
              />
            </FormControl>
          </div>
          <DataGridPro
            headerFilters
            columns={filteredGridColumns}
            rows={filteredAssignedUsers}
            loading={assignedUsersQuery.isFetching || assignedUsersQuery.isPending}
            pagination
            pageSizeOptions={[10, 20, 50, 100]}
            hideFooterSelectedRowCount
            headerFilterHeight={42}
            sx={gridStyle}
          />
        </Box>
        <NewVcAccountAssignmentsDialog isOpen={isAssignmentDialogOpen} onClose={() => setAssignmentDialogOpen(false)} />
        <DeleteVcAccountAssignmentDialog
          isOpen={deleteTarget !== null}
          vcAccount={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      </DeleteTargetContext.Provider>
    </ThemeProvider>
  );
};
